import { useState, Fragment, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, EyeIcon, EyeSlashIcon, FaceSmileIcon, FingerPrintIcon } from '@heroicons/react/24/outline'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { signIn, resetPassword } from '../lib/auth'
import { checkBiometricAvailability, saveBiometricCredentials, isBiometricEnabled, getBiometricCredentials } from '../lib/authPersistence'
import { Capacitor } from '@capacitor/core'
import logo from '../assets/wonderstood-high-resolution-logo-grayscale-transparent.png'

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToRegister?: () => void;
}

export default function SignInModal({ isOpen, onClose, onSwitchToRegister }: SignInModalProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetSuccess, setResetSuccess] = useState(false)
  const [resetError, setResetError] = useState('')
  const [isResetting, setIsResetting] = useState(false)
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false)
  const [biometricType, setBiometricType] = useState<string>('biometric')
  const [pendingCredentials, setPendingCredentials] = useState<{email: string, password: string} | null>(null)
  const [biometricAvailable, setBiometricAvailable] = useState(false)
  const [isBiometricLoading, setIsBiometricLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Check if biometric is available and enabled on mount
  useEffect(() => {
    const checkBiometric = async () => {
      if (Capacitor.isNativePlatform()) {
        const enabled = await isBiometricEnabled()
        const availability = await checkBiometricAvailability()
        if (enabled && availability.available) {
          setBiometricAvailable(true)
          setBiometricType(String(availability.biometryType || 'biometric'))
        }
      }
    }
    checkBiometric()
  }, [])

  // Check if user just verified their email
  const emailVerified = searchParams.get('verified') === 'true'
  const verificationError = searchParams.get('verificationError') === 'true'
  const expiredCode = searchParams.get('error') === 'expired'
  const prefilledEmail = searchParams.get('email') || ''

  // Pre-fill email if provided from verification
  useEffect(() => {
    if (prefilledEmail && !email) {
      setEmail(prefilledEmail)
    }
  }, [prefilledEmail])

  // Check if user was logged out due to session timeout
  const sessionTimeout = typeof window !== 'undefined' && localStorage.getItem('logoutReason') === 'session-timeout'

  // Clear logout reason on mount
  if (sessionTimeout && typeof window !== 'undefined') {
    localStorage.removeItem('logoutReason')
  }

  const sanitizeInput = (value: string, type: 'email' | 'password'): string => {
    let sanitized = value.replace(/[\x00-\x1F\x7F]/g, '');
    sanitized = sanitized.replace(/<[^>]*>/g, '');
    sanitized = sanitized.replace(/&[#\w]+;/g, '');

    if (type === 'email') {
      sanitized = sanitized.replace(/\s+/g, '').toLowerCase();
      sanitized = sanitized.slice(0, 254);
      sanitized = sanitized.replace(/[^a-z0-9@._-]/g, '');
    } else if (type === 'password') {
      sanitized = sanitized.trim();
      sanitized = sanitized.slice(0, 128);
      const dangerousPatterns = [/javascript:/i, /<script/i, /onload=/i, /onerror=/i, /onclick=/i, /eval\(/i, /expression\(/i];
      for (const pattern of dangerousPatterns) {
        if (pattern.test(sanitized)) return '';
      }
    }
    return sanitized;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      if (!email || email.length === 0) throw new Error('Email address is required');
      if (email.length < 5 || email.length > 254) throw new Error('Email address must be between 5 and 254 characters');
      if (!emailRegex.test(email)) throw new Error('Please enter a valid email address');
      if (!password || password.length === 0) throw new Error('Password is required');
      if (password.length > 128) throw new Error('Password must be less than 128 characters');

      const userCredential = await signIn(email, password);

      if (userCredential.user) {
        if (Capacitor.isNativePlatform()) {
          const alreadyEnabled = await isBiometricEnabled();
          if (!alreadyEnabled) {
            const availability = await checkBiometricAvailability();
            if (availability.available) {
              setPendingCredentials({ email, password });
              setBiometricType(String(availability.biometryType || 'biometric'));
              setShowBiometricPrompt(true);
              return;
            }
          }
        }

        setEmail('');
        setPassword('');
        onClose();

        const verified = searchParams.get('verified');
        setTimeout(() => {
          if (verified === 'true') {
            navigate('/app?setup=true');
          } else {
            navigate('/app');
          }
        }, 100);
      }
    } catch (error: any) {
      if (error.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (error.code === 'auth/user-disabled') {
        setError('This account has been disabled');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later');
      } else {
        setError(error.message || 'An error occurred during sign in');
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setError('');
    setIsLoading(false);
    setShowPassword(false);
    setShowForgotPassword(false);
    setResetEmail('');
    setResetSuccess(false);
    setResetError('');
    onClose();
  };

  const handleEnableBiometric = async () => {
    if (!pendingCredentials) return;
    try {
      await saveBiometricCredentials(pendingCredentials.email, pendingCredentials.password);
    } catch (error) {
      console.error('Failed to enable biometric login:', error);
    } finally {
      proceedAfterBiometricPrompt();
    }
  };

  const handleSkipBiometric = () => {
    proceedAfterBiometricPrompt();
  };

  const handleBiometricLogin = async () => {
    setIsBiometricLoading(true);
    setError('');
    try {
      const credentials = await getBiometricCredentials();
      if (credentials) {
        await signIn(credentials.email, credentials.password);
        onClose();
        navigate('/app');
      }
    } catch (error: any) {
      setError(error.message || 'Biometric authentication failed');
    } finally {
      setIsBiometricLoading(false);
    }
  };

  const proceedAfterBiometricPrompt = () => {
    setEmail('');
    setPassword('');
    setPendingCredentials(null);
    setShowBiometricPrompt(false);
    onClose();

    const verified = searchParams.get('verified');
    setTimeout(() => {
      if (verified === 'true') {
        navigate('/app?setup=true');
      } else {
        navigate('/app');
      }
    }, 100);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setIsResetting(true);

    try {
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      if (!resetEmail || resetEmail.length === 0) throw new Error('Email address is required');
      if (!emailRegex.test(resetEmail)) throw new Error('Please enter a valid email address');

      await resetPassword(resetEmail);
      setResetSuccess(true);
    } catch (error: any) {
      if (error.code === 'auth/user-not-found') {
        setResetError('No account found with this email address');
      } else if (error.code === 'auth/invalid-email') {
        setResetError('Invalid email address');
      } else if (error.code === 'auth/too-many-requests') {
        setResetError('Too many requests. Please try again later');
      } else {
        setResetError(error.message || 'Failed to send reset email');
      }
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <>
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-400" enterFrom="opacity-0 scale-95 translate-y-2" enterTo="opacity-100 scale-100 translate-y-0" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl liquid-glass shadow-2xl shadow-black/50 transition-all">
                {/* Header */}
                <div className="relative px-5 sm:px-6 py-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <img src={logo} alt="Wonderstood" className="h-5 sm:h-6 w-auto brightness-0 invert opacity-70" />
                    <button onClick={handleClose} className="rounded-full p-1.5 text-white/40 hover:text-white hover:bg-white/10 transition-all duration-200">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 sm:p-7">
                  {/* Title */}
                  <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-white mb-1">
                    Welcome <span className="font-serif italic">back</span>
                  </h2>
                  <p className="text-sm text-white/40 mb-7">Sign in to continue.</p>

                  {/* Status messages */}
                  {sessionTimeout && (
                    <div className="mb-5 rounded-xl bg-yellow-500/10 p-3.5 border border-yellow-500/20">
                      <p className="text-sm font-medium text-yellow-300">Session Expired</p>
                      <p className="text-xs text-yellow-400/70 mt-1">You were logged out due to 60 minutes of inactivity.</p>
                    </div>
                  )}
                  {emailVerified && (
                    <div className="mb-5 rounded-xl bg-green-500/10 p-3.5 border border-green-500/20">
                      <p className="text-sm font-medium text-green-300">Email Verified</p>
                      <p className="text-xs text-green-400/70 mt-1">
                        {prefilledEmail
                          ? `Your email ${prefilledEmail} has been verified. Enter your password to continue.`
                          : 'Please sign in with your email and password to complete setup.'}
                      </p>
                    </div>
                  )}
                  {expiredCode && (
                    <div className="mb-5 rounded-xl bg-yellow-500/10 p-3.5 border border-yellow-500/20">
                      <p className="text-sm font-medium text-yellow-300">Link Expired</p>
                      <p className="text-xs text-yellow-400/70 mt-1">This verification link has expired or was already used. Please sign in, and we can send you a new verification email if needed.</p>
                    </div>
                  )}
                  {verificationError && !expiredCode && (
                    <div className="mb-5 rounded-xl bg-yellow-500/10 p-3.5 border border-yellow-500/20">
                      <p className="text-sm text-yellow-300">There was an issue verifying your email. Please try signing in.</p>
                    </div>
                  )}
                  {error && (
                    <div className="mb-5 rounded-xl bg-red-500/10 p-3.5 border border-red-500/20">
                      <p className="text-sm text-red-300">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="signin-email" className="block text-sm font-medium text-white/80 mb-1.5">
                        Email address
                      </label>
                      <input
                        id="signin-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(sanitizeInput(e.target.value, 'email'))}
                        maxLength={254}
                        minLength={5}
                        spellCheck="false"
                        className="block w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:ring-1 focus:ring-white/20 focus:outline-none transition-all"
                        placeholder="name@email.com"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label htmlFor="signin-password" className="block text-sm font-medium text-white/80">
                          Password
                        </label>
                        <button
                          type="button"
                          onClick={() => { setResetEmail(email); setShowForgotPassword(true); }}
                          className="text-xs text-white/40 hover:text-white transition-colors"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          id="signin-password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          required
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(sanitizeInput(e.target.value, 'password'))}
                          maxLength={128}
                          spellCheck="false"
                          className="block w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 pr-11 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:ring-1 focus:ring-white/20 focus:outline-none transition-all"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-white/30 hover:text-white/60 transition-colors"
                        >
                          {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full rounded-full bg-white text-black py-3 text-sm font-semibold hover:scale-[1.02] hover:shadow-lg hover:shadow-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                    >
                      {isLoading ? 'Signing in...' : 'Sign in'}
                    </button>

                    {/* Biometric Login Button */}
                    {biometricAvailable && (
                      <>
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-white/10" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-transparent px-3 text-white/30 backdrop-blur-sm">Or</span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={handleBiometricLogin}
                          disabled={isBiometricLoading}
                          className="w-full flex justify-center items-center gap-2 rounded-full liquid-glass px-4 py-3 text-sm font-semibold text-white/80 hover:text-white hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                          {biometricType === 'faceID' ? (
                            <FaceSmileIcon className="h-5 w-5" />
                          ) : (
                            <FingerPrintIcon className="h-5 w-5" />
                          )}
                          {isBiometricLoading ? 'Authenticating...' : `Sign in with ${biometricType === 'faceID' ? 'Face ID' : biometricType === 'touchID' ? 'Touch ID' : 'Biometric'}`}
                        </button>
                      </>
                    )}
                  </form>
                </div>

                {/* Footer */}
                <div className="px-5 sm:px-7 py-5 border-t border-white/10">
                  <p className="text-center text-sm text-white/40">
                    Don't have an account?{' '}
                    <button
                      onClick={() => { handleClose(); onSwitchToRegister?.(); }}
                      className="text-white/70 hover:text-white font-medium transition-colors"
                    >
                      Register
                    </button>
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* Forgot Password Modal */}
    <Transition appear show={showForgotPassword} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => { setShowForgotPassword(false); setResetEmail(''); setResetSuccess(false); setResetError(''); }}
      >
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95 translate-y-2" enterTo="opacity-100 scale-100 translate-y-0" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl liquid-glass shadow-2xl shadow-black/50 transition-all">
                {/* Header */}
                <div className="relative px-5 sm:px-6 py-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <img src={logo} alt="Wonderstood" className="h-5 sm:h-6 w-auto brightness-0 invert opacity-70" />
                    <button
                      onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetSuccess(false); setResetError(''); }}
                      className="rounded-full p-1.5 text-white/40 hover:text-white hover:bg-white/10 transition-all duration-200"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 sm:p-7">
                  {resetSuccess ? (
                    <div className="text-center">
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/10 mb-4">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-medium text-white mb-2">Check your email</h3>
                      <p className="text-sm text-white/50 mb-1">
                        We've sent a password reset link to <span className="text-white/80">{resetEmail}</span>
                      </p>
                      <p className="text-sm text-white/40 mb-6">
                        The link will expire in 1 hour.
                      </p>
                      <button
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetSuccess(false); setResetError(''); }}
                        className="w-full rounded-full bg-white text-black py-3 text-sm font-semibold hover:scale-[1.02] transition-all duration-200"
                      >
                        Got it
                      </button>
                    </div>
                  ) : (
                    <div>
                      <h2 className="text-2xl font-medium tracking-tight text-white mb-1">
                        Reset <span className="font-serif italic">password</span>
                      </h2>
                      <p className="text-sm text-white/40 mb-6">
                        Enter your email and we'll send you a reset link.
                      </p>

                      {resetError && (
                        <div className="mb-5 rounded-xl bg-red-500/10 p-3.5 border border-red-500/20">
                          <p className="text-sm text-red-300">{resetError}</p>
                        </div>
                      )}

                      <form onSubmit={handleForgotPassword}>
                        <div>
                          <label htmlFor="reset-email" className="block text-sm font-medium text-white/80 mb-1.5">
                            Email address
                          </label>
                          <input
                            id="reset-email"
                            name="email"
                            type="email"
                            required
                            autoComplete="email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(sanitizeInput(e.target.value, 'email'))}
                            maxLength={254}
                            minLength={5}
                            spellCheck="false"
                            className="block w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/30 focus:ring-1 focus:ring-white/20 focus:outline-none transition-all"
                            placeholder="name@email.com"
                          />
                        </div>

                        <div className="mt-6 flex gap-3">
                          <button
                            type="button"
                            onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetError(''); }}
                            className="flex-1 rounded-full liquid-glass px-4 py-3 text-sm font-semibold text-white/70 hover:text-white transition-all"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isResetting}
                            className="flex-1 rounded-full bg-white text-black px-4 py-3 text-sm font-semibold hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          >
                            {isResetting ? 'Sending...' : 'Send reset link'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* Biometric Enrollment Prompt */}
    <Transition appear show={showBiometricPrompt} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={handleSkipBiometric}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95 translate-y-2" enterTo="opacity-100 scale-100 translate-y-0" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl liquid-glass shadow-2xl shadow-black/50 transition-all">
                {/* Header */}
                <div className="relative px-5 sm:px-6 py-6 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/10 border border-white/10 mb-4">
                    <FingerPrintIcon className="h-7 w-7 text-white/80" />
                  </div>
                  <Dialog.Title className="text-xl font-medium text-white">
                    Enable {biometricType === 'faceID' ? 'Face ID' : biometricType === 'touchID' ? 'Touch ID' : 'Biometric'} Login?
                  </Dialog.Title>
                  <p className="text-sm text-white/40 mt-1.5">
                    Sign in quickly and securely on this device
                  </p>
                </div>

                {/* Content */}
                <div className="px-5 sm:px-7 pb-7">
                  <div className="space-y-3.5 mb-7">
                    {[
                      { title: 'Quick sign in', desc: `Use ${biometricType === 'faceID' ? 'Face ID' : biometricType === 'touchID' ? 'Touch ID' : 'biometric authentication'} instead of entering your password` },
                      { title: 'Secure storage', desc: 'Your credentials are encrypted and stored securely on this device' },
                      { title: 'Stay signed in', desc: 'Remain signed in between app launches' },
                    ].map((item) => (
                      <div key={item.title} className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <svg className="h-4 w-4 text-white/60" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white/80">{item.title}</p>
                          <p className="text-xs text-white/40 mt-0.5">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleSkipBiometric}
                      className="flex-1 rounded-full liquid-glass px-4 py-3 text-sm font-semibold text-white/70 hover:text-white transition-all"
                    >
                      Not now
                    </button>
                    <button
                      type="button"
                      onClick={handleEnableBiometric}
                      className="flex-1 rounded-full bg-white text-black px-4 py-3 text-sm font-semibold hover:scale-[1.02] transition-all duration-200"
                    >
                      Enable
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
    </>
  )
}
